/**
 * Servicio para manejar el perfil del usuario en Firebase Realtime Database
 */

import { firebase } from '../config/firebase.config';
import { Storage } from '@capacitor/storage';
import ProfilePhotoService from './ProfilePhotoService';

export interface UserProfile {
  ced: string;
  username: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  eventcode: string;
  profilePhotoUrl?: string;
  updatedAt?: any;
}

class UserProfileService {
  private static instance: UserProfileService;
  private profilePhotoService: ProfilePhotoService;

  constructor() {
    this.profilePhotoService = ProfilePhotoService.getInstance();
  }

  static getInstance(): UserProfileService {
    if (!UserProfileService.instance) {
      UserProfileService.instance = new UserProfileService();
    }
    return UserProfileService.instance;
  }

  /**
   * Obtener el perfil del usuario actual desde Firebase
   */
  async getCurrentUserProfile(userced: string, eventcode: string): Promise<UserProfile | null> {
    try {
      console.log('UserProfileService: Obteniendo perfil para:', { userced, eventcode });

      // Buscar en la estructura /user de Firebase
      const usersSnapshot = await firebase.database().ref('/user').once('value');
      
      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();
        
        for (const userId in allUsers) {
          const userData = allUsers[userId];
          if (userData.ced === userced && userData.eventcode === eventcode) {
            console.log('UserProfileService: Perfil encontrado:', userData);
            return {
              ced: userData.ced,
              username: userData.username || '',
              email: userData.email || '',
              phone: userData.phone || '',
              company: userData.company || '',
              position: userData.role || userData.position || '',
              eventcode: userData.eventcode,
              profilePhotoUrl: userData.profilePhotoUrl || '',
              updatedAt: userData.updatedAt
            };
          }
        }
      }

      console.log('UserProfileService: No se encontró perfil para el usuario');
      return null;
    } catch (error) {
      console.error('UserProfileService: Error al obtener perfil:', error);
      return null;
    }
  }

  /**
   * Actualizar el perfil del usuario en Firebase
   */
  async updateUserProfile(userced: string, eventcode: string, updates: Partial<UserProfile>): Promise<boolean> {
    try {
      console.log('UserProfileService: Actualizando perfil:', { userced, eventcode, updates });

      // Buscar el usuario en Firebase para obtener su ID
      const usersSnapshot = await firebase.database().ref('/user').once('value');
      
      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();
        
        for (const userId in allUsers) {
          const userData = allUsers[userId];
          if (userData.ced === userced && userData.eventcode === eventcode) {
            // Actualizar el usuario encontrado (excluyendo position que no es editable)
            const { position, ...editableUpdates } = updates;
            const updateData = {
              ...editableUpdates,
              updatedAt: firebase.database.ServerValue.TIMESTAMP
            };

            await firebase.database().ref(`/user/${userId}`).update(updateData);
            
            console.log('UserProfileService: Perfil actualizado exitosamente');
            
            // También actualizar el perfil de networking si existe
            await this.updateNetworkingProfile(userced, eventcode, updates);
            
            return true;
          }
        }
      }

      console.log('UserProfileService: Usuario no encontrado para actualizar');
      return false;
    } catch (error) {
      console.error('UserProfileService: Error al actualizar perfil:', error);
      return false;
    }
  }

  /**
   * Actualizar también el perfil de networking para mantener consistencia
   */
  private async updateNetworkingProfile(userced: string, eventcode: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const networkingRef = firebase.database().ref(`networking_profiles/${eventcode}/${userced}`);
      
      // Verificar si existe el perfil de networking
      const snapshot = await networkingRef.once('value');
      
      if (snapshot.exists()) {
        const networkingUpdates: any = {};
        
        // Mapear los campos del perfil a los campos de networking
        if (updates.username) {
          networkingUpdates.name = updates.username;
        }

        if (updates.email) networkingUpdates.email = updates.email;
        if (updates.phone) networkingUpdates.phone = updates.phone;
        if (updates.company) networkingUpdates.company = updates.company;
        if (updates.profilePhotoUrl !== undefined) {
          networkingUpdates.profilePhotoUrl = updates.profilePhotoUrl;
        }
        // position no se actualiza porque no es editable por el usuario

        networkingUpdates.updatedAt = firebase.database.ServerValue.TIMESTAMP;
        
        await networkingRef.update(networkingUpdates);
        console.log('UserProfileService: Perfil de networking actualizado');
      }
    } catch (error) {
      console.warn('UserProfileService: Error al actualizar perfil de networking:', error);
    }
  }

  /**
   * Crear un nuevo perfil de usuario en Firebase
   */
  async createUserProfile(profile: UserProfile): Promise<boolean> {
    try {
      console.log('UserProfileService: Creando nuevo perfil:', profile);

      const newUserRef = await firebase.database().ref('/user').push({
        ...profile,
        createdAt: firebase.database.ServerValue.TIMESTAMP,
        updatedAt: firebase.database.ServerValue.TIMESTAMP
      });

      if (newUserRef.key) {
        console.log('UserProfileService: Perfil creado con ID:', newUserRef.key);
        return true;
      }

      return false;
    } catch (error) {
      console.error('UserProfileService: Error al crear perfil:', error);
      return false;
    }
  }

  /**
   * Guardar perfil en storage local para cache
   */
  async saveProfileToStorage(profile: UserProfile): Promise<void> {
    try {
      await Storage.set({
        key: 'user_profile',
        value: JSON.stringify(profile)
      });
      console.log('UserProfileService: Perfil guardado en storage local');
    } catch (error) {
      console.error('UserProfileService: Error al guardar en storage:', error);
    }
  }

  /**
   * Obtener perfil desde storage local
   */
  async getProfileFromStorage(): Promise<UserProfile | null> {
    try {
      const result = await Storage.get({ key: 'user_profile' });
      if (result.value) {
        return JSON.parse(result.value);
      }
      return null;
    } catch (error) {
      console.error('UserProfileService: Error al obtener desde storage:', error);
      return null;
    }
  }

  /**
   * Actualizar foto de perfil del usuario
   */
  async updateProfilePhoto(userced: string, eventcode: string, photoUrl: string): Promise<boolean> {
    try {
      console.log('UserProfileService: Actualizando foto de perfil:', { userced, eventcode, photoUrl });

      // Obtener el perfil actual para eliminar la foto anterior si existe
      const currentProfile = await this.getCurrentUserProfile(userced, eventcode);
      const oldPhotoUrl = currentProfile?.profilePhotoUrl;

      // Actualizar el perfil con la nueva foto
      const success = await this.updateUserProfile(userced, eventcode, {
        profilePhotoUrl: photoUrl
      });

      if (success && oldPhotoUrl && oldPhotoUrl !== photoUrl) {
        // Eliminar la foto anterior de Firebase Storage (opcional)
        await this.profilePhotoService.deleteOldPhoto(oldPhotoUrl);
      }

      return success;
    } catch (error) {
      console.error('UserProfileService: Error al actualizar foto de perfil:', error);
      return false;
    }
  }

  /**
   * Obtener URL de foto de perfil del usuario
   */
  async getProfilePhotoUrl(userced: string, eventcode: string): Promise<string> {
    try {
      const profile = await this.getCurrentUserProfile(userced, eventcode);
      return profile?.profilePhotoUrl || this.profilePhotoService.getDefaultProfilePhoto();
    } catch (error) {
      console.error('UserProfileService: Error al obtener foto de perfil:', error);
      return this.profilePhotoService.getDefaultProfilePhoto();
    }
  }

  /**
   * Eliminar foto de perfil del usuario
   */
  async removeProfilePhoto(userced: string, eventcode: string): Promise<boolean> {
    try {
      console.log('UserProfileService: Eliminando foto de perfil:', { userced, eventcode });

      // Obtener la URL de la foto actual
      const currentProfile = await this.getCurrentUserProfile(userced, eventcode);
      const photoUrl = currentProfile?.profilePhotoUrl;

      // Actualizar el perfil para remover la foto
      const success = await this.updateUserProfile(userced, eventcode, {
        profilePhotoUrl: ''
      });

      if (success && photoUrl) {
        // Eliminar la foto de Firebase Storage
        await this.profilePhotoService.deleteOldPhoto(photoUrl);
      }

      return success;
    } catch (error) {
      console.error('UserProfileService: Error al eliminar foto de perfil:', error);
      return false;
    }
  }
}

export default UserProfileService;
